// The iOS platform copy (spec section 6.8): one Keychain item per member id
// through @aparajita/capacitor-secure-storage with synchronisation on, so the
// item carries `kSecAttrSynchronizable` and iCloud Keychain moves it to a
// restored or replaced phone. Keychain items also survive an uninstall, so a
// reinstall finds the key. Nothing else in the app is stored through this
// plugin.
//
// Eligibility means the Keychain accepted a probe item. It cannot mean more:
// iOS offers no API that says whether iCloud Keychain is on, and the plugin's
// `getSynchronize()` only echoes the flag this app set, so with iCloud
// Keychain off the item saves locally, the probe passes, and the key stays on
// this phone. The nudge copy therefore always asks the member to check that
// iCloud Keychain is on (decision 17, as built).
import { ref } from 'vue';
import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';
import { PLATFORM_COPY_PROBE_KEY, platformCopyKey } from './platform-copy';

/**
 * @param {object} [deps]
 * @param {typeof SecureStorage} [deps.storage] the plugin, replaceable in specs
 * @returns {import('./platform-key-adapter').PlatformKeyAdapter}
 */
export function createIosPlatformAdapter({ storage = SecureStorage } = {}) {
  const eligible = ref(false);
  let ready = null;

  /**
   * Before the first Keychain call: synchronisation on, and items readable
   * after the first unlock rather than only while unlocked, so a launch from
   * a locked-then-unlocked phone does not misread the key as absent. Both
   * classes migrate to a new device and sync.
   */
  const init = () => {
    if (!ready) {
      ready = (async () => {
        await storage.setSynchronize(true);
        await storage.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlock);
      })().catch(error => {
        ready = null;
        throw error;
      });
    }
    return ready;
  };

  /** Writes, reads back and removes a probe item; true when the Keychain kept it. */
  const roundTrip = async () => {
    const token = `probe:${Date.now()}`;
    try {
      await storage.setItem(PLATFORM_COPY_PROBE_KEY, token);
      return (await storage.getItem(PLATFORM_COPY_PROBE_KEY)) === token;
    } finally {
      await storage.removeItem(PLATFORM_COPY_PROBE_KEY).catch(() => {});
    }
  };

  return {
    kind: 'ios',
    isEligible: () => eligible.value,
    async probe() {
      try {
        await init();
        eligible.value = await roundTrip();
      } catch (error) {
        console.warn('[CoursesKeys] iOS Keychain probe failed:', error);
        eligible.value = false;
      }
      return eligible.value;
    },
    async put(memberId, copy, slot) {
      const key = platformCopyKey(memberId, slot);
      await init();
      await storage.setItem(key, copy);
    },
    async get(memberId, slot) {
      const key = platformCopyKey(memberId, slot);
      await init();
      // The plugin answers null for an absent item; any error is a real one
      // and goes up, so hydration can fail loudly rather than read absence.
      return (await storage.getItem(key)) ?? null;
    },
    async remove(memberId, slot) {
      const key = platformCopyKey(memberId, slot);
      await init();
      await storage.removeItem(key);
    },
  };
}
